import SelectableCard from 'core/components/SelectableCard'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import React, { useCallback, useEffect, useState } from 'react'
import { updateClusterHostGroup } from './actions'
import { useStyles } from './CreateClusterHostGroupsModal'
import {
  EditClusterHostGroup,
  IClusterHostGroup,
  RebalancingFrequency,
  RebalancingStrategy,
} from './model'
import RebalancingFrequencyPickList from './RebalancingFrequencyPickList'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import { checkVMHAStatusForCluster } from '../actions'
import { mapVMHAConditions } from './helpers'
import VmhaConditionsStatus from './VmhaConditionsStatus'
import ExternalLink from 'core/components/ExternalLink'
import { vmhaClusterHostRequirementsDoc } from 'app/docLinks'

const gpuModes = [
  { label: 'Passthrough', value: 'passthrough' },
  { label: 'vGPU', value: 'vgpu' },
]

interface Props {
  rows: IClusterHostGroup[]
  onClose: (reload?: boolean) => void
  isVmhaModalView?: boolean
}

export default function EditClusterHostGroupModal({
  rows,
  onClose,
  isVmhaModalView = false,
}: Props) {
  const classes = useStyles()
  const [gpuError, setGPUError] = useState<string | null>(null)
  const [vmhaClusterDetails, setVmhaClusterDetails] = useState()
  const [vmhaDisabled, setVmhaDisabled] = useState(false)
  const [vmhaClusterStatusLoading, setVmhaClusterStatusLoading] = useState(false)
  const defaultParams = {
    name: rows?.[0]?.name,
    vmHighAvailability: rows?.[0]?.vmHighAvailability?.enabled,
    autoResourceRebalancing: rows?.[0]?.autoResourceRebalancing?.enabled,
    rebalancingFrequency:
      rows?.[0]?.autoResourceRebalancing?.rebalancingFrequencyMins || RebalancingFrequency.Default,
    enableGpu: rows?.[0]?.gpu?.enabled,
    gpuType: rows?.[0]?.gpu?.mode,
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)
  const { update, updating: updatingClusterHostGroup, error, reset } = useUpdateAction(
    updateClusterHostGroup,
  )

  const fetchVmhaClusterStatus = useCallback(async () => {
    if (!rows?.[0]?.name) return

    try {
      setVmhaClusterStatusLoading(true)

      const configs: any = await checkVMHAStatusForCluster(rows?.[0]?.name)

      const conditions = mapVMHAConditions(configs)
      const hasFailure = conditions?.some((c) => !c.passed)

      setVmhaDisabled(hasFailure)
      setVmhaClusterDetails(configs)
    } catch (error) {
      console.error(error)
    } finally {
      setVmhaClusterStatusLoading(false)
    }
  }, [rows?.[0]?.name])

  useEffect(() => {
    fetchVmhaClusterStatus()
  }, [fetchVmhaClusterStatus])

  // When enabling GPU, check if all hosts support the selected GPU mode
  useEffect(() => {
    if (
      params?.enableGpu &&
      params?.gpuType &&
      rows?.[0]?.hosts?.length > 0 &&
      rows?.[0]?.hosts?.some((host) => !host?.compatibleModes?.includes(params.gpuType))
    ) {
      setGPUError(`All hosts in the cluster must support the selected GPU mode.`)
    } else {
      setGPUError(null)
    }
  }, [params.gpuType, params.enableGpu])

  const handleClose = useCallback(() => {
    setParams(defaultParams)
    reset()
    onClose()
  }, [setParams, reset, onClose])

  const submitForm = useCallback(async () => {
    const body: EditClusterHostGroup = {
      name: params.name,
      vmHighAvailability: { enabled: isVmhaModalView ? true : params.vmHighAvailability },
      autoResourceRebalancing: {
        enabled: params.autoResourceRebalancing,
        ...(params.autoResourceRebalancing && {
          rebalancingStrategy: RebalancingStrategy.VMWorkloadConsolidation,
          rebalancingFrequencyMins: params.rebalancingFrequency,
        }),
      },
      gpu: {
        enabled: params.enableGpu,
        mode: params.gpuType,
      },
    }
    const { success } = await update({ body })
    if (success) {
      handleClose()
    }
  }, [params, update, handleClose])

  return (
    <ModalForm
      open={true}
      title={isVmhaModalView ? `Enable VM HA` : `Edit Cluster`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updatingClusterHostGroup}
      error={error}
      initialValues={defaultParams}
      submitTitle={isVmhaModalView ? `Confirm` : `Update Cluster`}
      disableSubmit={!!gpuError}
      panel={isVmhaModalView ? 'dialog' : 'drawer'}
      maxWidth={isVmhaModalView && 420}
      className={isVmhaModalView ? classes.vmhaModal : undefined}
    >
      {isVmhaModalView ? (
        <>
          <div className={classes.textContainer}>
            <Text variant="body2">
              All prerequisites are met. VM HA helps protect your workloads by automatically
              restarting VMs on another host if a failure occurs.
            </Text>
            <Text variant="body2">Confirm to enable this protection for your cluster.</Text>
          </div>
        </>
      ) : (
        <>
          {/* <div className={classes.header}>
          <img alt="host group" src="/ui/images/host-group.svg" />
          <Text variant="body2">
            Create a new host group to organize and manage hosts for workload deployment. You can
            configure metadata and host allocation ratios to optimize resource usage.
          </Text>
        </div> */}
          <FormFieldSection
            title="Cluster Configuration"
            className={classes.customFormFieldSection}
          >
            <TextField
              id="name"
              label="Name"
              onChange={getParamsUpdater('name')}
              value={params.name}
              readOnly
            />
            <div className={classes.toggleSwitch}>
              <ToggleSwitchField
                id="vmHighAvailability"
                label="VM High Availability"
                value={params.vmHighAvailability}
                onChange={(value) => getParamsUpdater('vmHighAvailability')(value)}
                customLabel={
                  <div className={classes.customLabel}>
                    <Text variant="caption1" className={classes.customLabelTitle}>
                      VM High Availability
                      <HintTooltip message="Automatically detect host failure and recover VMs on other host group hosts." />
                    </Text>
                  </div>
                }
                disabled={
                  !defaultParams.vmHighAvailability && !vmhaClusterStatusLoading && vmhaDisabled
                }
              />
              <div className={classes.vmhaToggleSwitchWarning}>
                <VmhaConditionsStatus
                  title={
                    defaultParams.vmHighAvailability ? (
                      <>
                        The cluster will reach 'Protected' status only when all prerequisites below
                        are satisfied. Partial or unmet conditions will result in 'Degraded' or 'Not
                        Protected' states.{' '}
                        <ExternalLink
                          variant="caption2"
                          url={vmhaClusterHostRequirementsDoc}
                          icon="external-link"
                        >
                          <b>More Info</b>
                        </ExternalLink>
                      </>
                    ) : (
                      'All conditions must be met to enable VMHA -'
                    )
                  }
                  conditions={mapVMHAConditions(vmhaClusterDetails)}
                  loading={vmhaClusterStatusLoading}
                  plainText
                />
              </div>
            </div>

            <div className={classes.toggleSwitch}>
              <ToggleSwitchField
                id="autoResourceRebalancing"
                label="Dynamic Resource Rebalancing"
                value={params.autoResourceRebalancing}
                onChange={(value) => getParamsUpdater('autoResourceRebalancing')(value)}
                customLabel={
                  <div className={classes.customLabel}>
                    <Text variant="caption1" className={classes.customLabelTitle}>
                      Dynamic Resource Rebalancing
                      <HintTooltip message="Dynamically rebalances workloads across hosts in the host group to optimize resource utilization and prevent overload." />
                    </Text>
                    <Text variant="body2">
                      Dynamically rebalance workloads across hosts in the host group.
                    </Text>
                  </div>
                }
              />
            </div>
            {params.autoResourceRebalancing && (
              <RebalancingFrequencyPickList
                value={params.rebalancingFrequency}
                onChange={(value) => getParamsUpdater('rebalancingFrequency')(value)}
                className={classes.rebalancingFrequency}
              />
            )}
            <div className={classes.toggleSwitch}>
              <ToggleSwitchField
                id="enableGpu"
                label="Enable GPU"
                value={params.enableGpu}
                onChange={(value) => {
                  updateParams({ enableGpu: value, gpuType: gpuModes[0].value })
                }}
                customLabel={
                  <div className={classes.customLabel}>
                    <Text variant="caption1" className={classes.customLabelTitle}>
                      Enable GPU
                      <HintTooltip message="Enable GPU support for this cluster to allow GPU passthrough, or vGPU-based virtual machines. This must be turned on before GPU configuration or assignment." />
                    </Text>
                    <Text variant="body2">
                      Use this feature to enable GPU support for the cluster.
                    </Text>
                  </div>
                }
              />
            </div>
            {params.enableGpu && (
              <div className={classes.gpuFields}>
                <Text variant="subtitle2">GPU Mode</Text>
                <Text variant="body2">
                  Choose the type of GPU support you want this flavor to provide. Use{' '}
                  <b>Passthrough</b> to assign full physical GPUs, or <b>vGPU</b> to use virtual GPU
                  slices.
                </Text>
                <div className={classes.gpuCards}>
                  {gpuModes.map(({ label, value }) => (
                    <SelectableCard
                      id={value}
                      onClick={() => updateParams({ gpuType: value })}
                      active={params.gpuType === value}
                      withCustomBody
                      showRadioButton
                      className={classes.card}
                      // disabled={disabled}
                      // showCheckmarkIcon
                    >
                      <Text variant="caption1">{label}</Text>
                    </SelectableCard>
                  ))}
                </div>
                {gpuError && <ErrorMessage>{gpuError}</ErrorMessage>}
              </div>
            )}
            {/* @TODO: API does not support metadata. Commenting out for now. https://platform9.atlassian.net/browse/PCD-1508 */}
            {/* <KeyValuesField
            id="metadata"
            label="Metadata"
            value={params.metadata}
            onChange={getParamsUpdater('metadata')}
            addLabel="Add Metadata"
            allowMultipleValues
          /> */}
          </FormFieldSection>
        </>
      )}
    </ModalForm>
  )
}
